import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const VIDEOS = [
  { src: "/videos/vera.mp4", label: "Vera" },
  { src: "/videos/Verasist.mp4", label: "Verasist" },
  { src: "/videos/demo.mp4", label: "Demo" },
  { src: "/videos/verasist-2.mp4", label: "Verasist 2" },
];

const VideoCollageSection = () => {
  const sectionRef = useRef(null);
  const carouselRef = useRef(null);
  const videoRefs = useRef([]);
  const hideTimer = useRef(null);
  const [inView, setInView] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [showControls, setShowControls] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const showControlsTemp = () => {
    setShowControls(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowControls(false), 3000);
  };

  useEffect(() => {
    if (isMobile) showControlsTemp();
    return () => clearTimeout(hideTimer.current);
  }, [isMobile]);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.3 }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      video.muted = isMuted;
      if (index !== activeIndex || !inView) video.pause();
    });

    const activeVideo = videoRefs.current[activeIndex];
    if (!activeVideo) return;

    if (inView) {
      const playPromise = activeVideo.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch(() => setIsPlaying(false));
      }
    } else {
      setIsPlaying(false);
    }
  }, [activeIndex, inView, isMuted]);

  const goToSlide = (index) => {
    const nextIndex = (index + VIDEOS.length) % VIDEOS.length;
    setActiveIndex(nextIndex);
    carouselRef.current?.children[nextIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "start",
    });
  };

  const handleScroll = () => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    const index = Math.round(carousel.scrollLeft / carousel.clientWidth);
    if (index !== activeIndex) setActiveIndex(index);
  };

  const togglePlay = () => {
    const video = videoRefs.current[activeIndex];
    if (!video) return;
    if (isMobile) showControlsTemp();

    if (video.paused) {
      video.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    if (isMobile) showControlsTemp();
    setIsMuted((muted) => !muted);
  };

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden"
      style={{ height: "100svh", minHeight: "600px", background: "transparent" }}
      onMouseEnter={() => !isMobile && setIsHovering(true)}
      onMouseLeave={() => !isMobile && setIsHovering(false)}
      onTouchStart={() => isMobile && showControlsTemp()}
    >
      <div
        ref={carouselRef}
        onScroll={handleScroll}
        className="flex h-full w-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain"
        style={{ scrollbarWidth: "none" }}
      >
        {VIDEOS.map((video, index) => (
          <div key={video.src} className="relative h-full min-w-full snap-start">
            <video
              ref={(element) => {
                videoRefs.current[index] = element;
              }}
              className={`absolute inset-0 h-full w-full ${
                isMobile ? "object-contain" : "object-cover"
              }`}
              src={video.src}
              muted={isMuted}
              playsInline
              preload={index === 0 ? "auto" : "metadata"}
              onEnded={() => goToSlide(index + 1)}
              onPlay={() => index === activeIndex && setIsPlaying(true)}
              onPause={() => index === activeIndex && setIsPlaying(false)}
              onVolumeChange={(event) => setIsMuted(event.target.muted)}
            />
          </div>
        ))}
      </div>

      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, var(--bg) 0%, transparent 15%, transparent 82%, var(--bg) 100%)",
        }}
      />

      <motion.div
        className="absolute inset-0 z-10 flex items-center justify-center gap-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: isMobile ? (showControls ? 1 : 0) : isHovering ? 1 : 0 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.05) 70%)",
          pointerEvents: "none",
        }}
      >
        <button
          onClick={togglePlay}
          aria-label={isPlaying ? "Durdur" : "Oynat"}
          className="flex h-12 w-12 items-center justify-center rounded-full transition-all duration-200 hover:scale-110 active:scale-95 md:h-14 md:w-14"
          style={{
            background: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.25)",
            pointerEvents: "auto",
          }}
        >
          {isPlaying ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          )}
        </button>

        <button
          onClick={toggleMute}
          aria-label={isMuted ? "Sesi aç" : "Sesi kapat"}
          className="flex h-12 w-12 items-center justify-center rounded-full transition-all duration-200 hover:scale-110 active:scale-95 md:h-14 md:w-14"
          style={{
            background: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.25)",
            pointerEvents: "auto",
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            {isMuted ? (
              <>
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </>
            ) : (
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
            )}
          </svg>
        </button>
      </motion.div>

      <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3">
        <button
          onClick={() => goToSlide(activeIndex - 1)}
          aria-label="Önceki video"
          className="flex h-9 w-9 items-center justify-center rounded-full text-white transition hover:bg-white/20"
        >
          <span aria-hidden="true">←</span>
        </button>
        {VIDEOS.map((video, index) => (
          <button
            key={video.src}
            onClick={() => goToSlide(index)}
            aria-label={`${video.label} videosuna git`}
            className="h-2 rounded-full transition-all duration-300"
            style={{
              width: index === activeIndex ? "28px" : "8px",
              background: index === activeIndex ? "var(--fg)" : "rgba(255,255,255,0.45)",
            }}
          />
        ))}
        <button
          onClick={() => goToSlide(activeIndex + 1)}
          aria-label="Sonraki video"
          className="flex h-9 w-9 items-center justify-center rounded-full text-white transition hover:bg-white/20"
        >
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </section>
  );
};

export default VideoCollageSection;
